import Vue from 'vue'
import Router from 'vue-router'
import AuthLayout from '@/layouts/Auth'
import MainLayout from '@/layouts/Main'
import PassThrough from '@/layouts/PassThrough'
import store from '@/store'

Vue.use(Router)

const router = new Router({
  base: process.env.BASE_URL,
  mode: 'history',
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      redirect: 'dashboard',
      component: MainLayout,
      name: 'dashboard',
      meta: {
        authRequired: true,
        hidden: true,
      },
      children: [
        {
          path: '/dashboard',
          meta: {
            title: 'Dashboard',
          },
          component: () => import('./views/dashboard/main'),
        },
        {
          path: 'store',
          component: PassThrough,
          name: 'store',
          children: [
            {
              path: '/store/store-settings',
              name: 'store.settings',
              meta: {
                title: 'Store Settings',
              },
              component: () => import('@/views/store-setting'),
            },
            {
              path: '/store/available-products',
              name: 'available.products',
              meta: {
                title: 'Available Products',
              },
              component: () => import('./views/store/available-products'),
            },
            {
              path: '/store/my-products',
              name: 'my.products',
              meta: {
                title: 'My Products',
              },
              component: () => import('@/views/store/my-products'),
            },
          ],
        },
        {
          path: '/product/:id',
          name: 'product.detail',
          component: () => import('@/components/Product/Detail'),
        },
        {
          path: '/sales/:status?',
          name: 'order.index',
          meta: {
            title: 'Sales Order',
          },
          component: () => import('./views/Order/Index'),
          children: [
            {
              path: '/sales/view/:id?',
              name: 'order.view',
              meta: {
                title: 'Sales Order',
              },
              component: () => import('./views/Order/Detail'),
            },
          ],
        },
        {
          path: 'account',
          component: PassThrough,
          name: 'account',
          children: [
            {
              path: '/account/edit-profile',
              name: 'edit.profile',
              meta: {
                title: 'Edit Profile',
              },
              component: () => import('@/views/account/edit-profile'),
            },
            {
              path: '/account/list-commission',
              name: 'list.commission',
              meta: {
                title: 'List Commission',
              },
              component: () => import('@/views/account/list-commission'),
            },
            {
              path: '/account/withdraw-request',
              name: 'withdraw.request',
              meta: {
                title: 'Withdraw Request',
              },
              component: () => import('@/views/account/withdraw-request'),
            },
            {
              path: '/account/ewallet',
              name: 'ewallet.history',
              meta: {
                title: 'Ewallet History',
              },
              component: () => import('@/views/account/ewallet-history'),
            },
            {
              path: '/account/change-password',
              name: 'change.password',
              meta: {
                title: 'Change Password',
              },
              component: () => import('@/views/account/change-password'),
            },
          ],
        },
        {
          path: 'purchase',
          component: PassThrough,
          name: 'purchase',
          redirect: '/',
          children: [
            {
              path: '/purchase',
              name: 'purchase.index',
              meta: {
                title: 'Purchase List',
              },
              component: () => import('./views/purchase/index'),
            },
            {
              path: '/purchase/add',
              name: 'purchase.create',
              meta: {
                title: 'Create Purchase',
              },
              component: () => import('@/views/purchase/create'),
            },
            {
              path: '/purchase/catalogue',
              name: 'purchase.catalogue',
              meta: {
                title: 'Catalogue List',
              },
              component: () => import('@/views/purchase/catalogue'),
            },
            {
              path: '/purchase/order/:order_id',
              name: 'purchase.success',
              component: () => import('@/views/purchase/detail'),
            },
          ],
        },
      ],
    },

    // System Pages
    {
      path: '/auth',
      component: AuthLayout,
      redirect: 'auth/login',
      children: [
        {
          path: '/auth/404',
          meta: {
            title: 'Error 404',
          },
          component: () => import('./views/auth/404'),
        },
        {
          path: '/auth/500',
          meta: {
            title: 'Error 500',
          },
          component: () => import('./views/auth/500'),
        },
        {
          path: '/auth/login',
          meta: {
            title: 'Sign In',
          },
          component: () => import('./views/auth/login'),
        },
        {
          path: '/auth/forgot-password',
          meta: {
            title: 'Forgot Password',
          },
          component: () => import('./views/auth/forgot-password'),
        },
        {
          path: '/reset-password/:hashId/:resetLink',
          meta: {
            title: 'Forgot Password',
          },
          component: () => import('./views/auth/reset-password'),
        },
        {
          path: '/auth/register',
          meta: {
            title: 'Register',
          },
          component: () => import('./views/auth/register'),
        },
        {
          path: '/auth/otp',
          name: 'otp',
          meta: {
            title: 'OTP confirmation',
          },
          component: () => import('./views/auth/otp'),
        },
      ],
    },
    {
      path: '/reg-conf/:linkId',
      meta: {
        title: 'Register Confirmation',
      },
      component: () => import('./views/auth/register-confirmation'),
    },

    {
      path: '/link-expired',
      hidden: true,
      component: () => import('./views/auth/link-expired'),
    },

    // Redirect to 404
    {
      path: '*', redirect: 'auth/404', hidden: true,
    },
  ],
})

router.beforeEach((to, from, next) => {
  if (!store.state.app.id && to.path !== '/auth/404' && !store.state.app.fetched) {
    return next('/auth/404')
  }

  if (to.matched.some(record => record.meta && record.meta.authRequired)) {
    const exp = store.getters['user/expireIn']

    if (exp < (new Date().getTime() / 1000)) {
      store.commit('user/LOGOUT')
    }

    if (!store.getters['user/authorized']) {
      return next({
        path: '/auth/login',
        query: { redirect: to.fullPath },
      })
    }
  }

  next()
})

export default router
